interface BrowserInfo {
  browser: string;
  os: string;
  mobile: boolean;
  version?: string;
}

/**
 * Get information about the user's browser and operating system
 */
export const getBrowserInfo = (): BrowserInfo => {
  const userAgent = navigator.userAgent;
  let browser = 'Unknown';
  let os = 'Unknown';
  let version = '';
  let mobile = false;

  // Detect browser
  if (userAgent.includes('Firefox')) {
    browser = 'Firefox';
    const match = userAgent.match(/Firefox\/([0-9.]+)/);
    if (match) version = match[1];
  } else if (userAgent.includes('SamsungBrowser')) {
    browser = 'Samsung Internet';
    const match = userAgent.match(/SamsungBrowser\/([0-9.]+)/);
    if (match) version = match[1];
  } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
    browser = 'Opera';
    const match = userAgent.match(/(?:Opera|OPR)\/([0-9.]+)/);
    if (match) version = match[1];
  } else if (userAgent.includes('Edg')) {
    browser = 'Microsoft Edge';
    const match = userAgent.match(/Edg\/([0-9.]+)/);
    if (match) version = match[1];
  } else if (userAgent.includes('Chrome')) {
    browser = 'Chrome';
    const match = userAgent.match(/Chrome\/([0-9.]+)/);
    if (match) version = match[1];
  } else if (userAgent.includes('Safari')) {
    browser = 'Safari';
    const match = userAgent.match(/Version\/([0-9.]+)/);
    if (match) version = match[1];
  } else if (userAgent.includes('MSIE') || userAgent.includes('Trident/')) {
    browser = 'Internet Explorer';
    const match = userAgent.match(/(?:MSIE |rv:)([0-9.]+)/);
    if (match) version = match[1];
  }

  // Detect OS
  if (userAgent.includes('Windows NT')) {
    os = 'Windows';
    const match = userAgent.match(/Windows NT ([0-9.]+)/);
    if (match) {
      const version = match[1];
      switch (version) {
        case '10.0': os = 'Windows 10'; break;
        case '6.3': os = 'Windows 8.1'; break;
        case '6.2': os = 'Windows 8'; break;
        case '6.1': os = 'Windows 7'; break;
        case '6.0': os = 'Windows Vista'; break;
        case '5.2': os = 'Windows XP 64-bit'; break;
        case '5.1': os = 'Windows XP'; break;
        default: os = `Windows (${version})`;
      }
    }
  } else if (userAgent.includes('Macintosh')) {
    os = 'macOS';
    const match = userAgent.match(/Mac OS X ([0-9_]+)/);
    if (match) {
      const macVersion = match[1].replace(/_/g, '.');
      os = `macOS ${macVersion}`;
    }
  } else if (userAgent.includes('Linux')) {
    os = 'Linux';
    if (userAgent.includes('Android')) {
      os = 'Android';
      const match = userAgent.match(/Android ([0-9.]+)/);
      if (match) os = `Android ${match[1]}`;
    }
  } else if (userAgent.includes('iPhone')) {
    os = 'iOS';
    const match = userAgent.match(/OS ([0-9_]+)/);
    if (match) {
      const iosVersion = match[1].replace(/_/g, '.');
      os = `iOS ${iosVersion}`;
    }
  } else if (userAgent.includes('iPad')) {
    os = 'iPadOS';
    const match = userAgent.match(/OS ([0-9_]+)/);
    if (match) {
      const iosVersion = match[1].replace(/_/g, '.');
      os = `iPadOS ${iosVersion}`;
    }
  }

  // Detect if mobile
  mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

  return {
    browser,
    os,
    mobile,
    version
  };
};

/**
 * Get screen dimensions and device pixel ratio
 */
export const getScreenInfo = (): { width: number; height: number; pixelRatio: number } => {
  return {
    width: window.screen.width,
    height: window.screen.height,
    pixelRatio: window.devicePixelRatio
  };
};

// Define a type for browser connection information
interface ConnectionInfo {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

/**
 * Get connection information if available
 */
export const getConnectionInfo = (): { type?: string; downlink?: number; rtt?: number } => {
  // Cast navigator to include potential connection properties
  const nav = navigator as Navigator & {
    connection?: ConnectionInfo;
    mozConnection?: ConnectionInfo;
    webkitConnection?: ConnectionInfo;
  };
  
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
  
  if (connection) {
    return {
      type: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt
    };
  }
  
  return {};
};

/**
 * Get comprehensive device information for analytics
 */
export const getDeviceInfo = () => {
  const browserInfo = getBrowserInfo();
  const screenInfo = getScreenInfo();
  const connectionInfo = getConnectionInfo();

  return {
    deviceType: browserInfo.mobile ? 'mobile' : 'desktop',
    browser: browserInfo.browser,
    browserVersion: browserInfo.version,
    os: browserInfo.os,
    mobile: browserInfo.mobile,
    screenResolution: `${screenInfo.width}x${screenInfo.height}`,
    pixelRatio: screenInfo.pixelRatio,
    connectionType: connectionInfo.type,
    downlink: connectionInfo.downlink,
    rtt: connectionInfo.rtt
  };
};